import { ref, computed } from 'vue'
import axios from '@/services/axios.js'
import { isCancel } from 'axios'
import debounce from 'debounce'

export function useAutocomplete(props) {
  const searchTerm = ref('')
  const inputRef = ref(null)
  const suggestions = ref([])
  const showSuggestions = ref(false)
  const apiError = ref(null)
  const isLoading = ref(false)
  let controller = null
  let inFlightQuery = null

  const chevronIcon = computed(() =>
    showSuggestions.value ? 'chevron-up' : 'chevron-down'
  )

  const closeSuggestions = () => {
    showSuggestions.value = false
  }

  const toggleSuggestionsAndFocus = () => {
    if (showSuggestions.value) {
      closeSuggestions()
    } else {
      inputRef.value?.focus()
      fetchSuggestions(searchTerm.value)
    }
  }

  const fetchSuggestions = async (query) => {
    if (
      (!query && !props.searchOnFocus) ||
      (query?.length && query?.length < props.minChars)
    ) {
      // Abort any in-flight request so a stale response can't
      // repopulate the suggestions after they have been cleared
      controller?.abort()
      controller = null
      suggestions.value = []
      closeSuggestions()
      apiError.value = null
      isLoading.value = false
      return
    }

    // Let an identical in-flight request finish instead of restarting it
    if (controller && query === inFlightQuery) return

    controller?.abort()
    const thisController = new AbortController()
    controller = thisController
    inFlightQuery = query

    isLoading.value = true
    apiError.value = null
    showSuggestions.value = true

    try {
      const { data } = await axios.get(props.apiEndpoint, {
        params: { q: query },
        signal: thisController.signal,
      })
      suggestions.value = data
    } catch (error) {
      if (!isCancel(error)) {
        console.error('Error fetching suggestions:', error)
        suggestions.value = []
        apiError.value =
          error.response?.data?.error || 'Failed to fetch suggestions'
      }
    } finally {
      // An aborted request has been superseded and no longer owns the state
      if (controller === thisController) {
        isLoading.value = false
        controller = null
      }
    }
  }

  const debouncedFetch = debounce(fetchSuggestions, 300)

  const onInput = (event) => debouncedFetch(event.target.value)

  const cleanup = () => {
    controller?.abort()
  }

  return {
    searchTerm,
    suggestions,
    showSuggestions,
    apiError,
    isLoading,
    chevronIcon,
    toggleSuggestionsAndFocus,
    closeSuggestions,
    fetchSuggestions,
    onInput,
    cleanup,
    inputRef,
  }
}
